import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { accountApi } from "./accountApi";

/**
 * Stato di sessione condiviso da tutte le pagine.
 *
 * `account` vale `undefined` finche' non si sa (prima lettura di
 * /api/account/io), `null` se non c'e' una sessione, altrimenti il profilo
 * restituito dal backend (utente, azienda, funzioni, consumo, team).
 */
const Contesto = createContext(null);

const VUOTO = {
  account: null,
  caricamento: false,
  errore: null,
  ricarica: async () => null,
  esci: async () => {},
  imposta: () => {},
  api: accountApi,
};

export function AccountProvider({ children, api = accountApi }) {
  const [account, setAccount] = useState(undefined);
  const [errore, setErrore] = useState(null);

  const ricarica = useCallback(async () => {
    try {
      const profilo = await api.io();
      setAccount(profilo);
      setErrore(null);
      return profilo;
    } catch (e) {
      setAccount(null);
      setErrore(e.message);
      return null;
    }
  }, [api]);

  useEffect(() => { ricarica(); }, [ricarica]);

  const esci = useCallback(async () => {
    try { await api.uscita(); } finally { setAccount(null); }
  }, [api]);

  const valore = useMemo(() => ({
    account: account === undefined ? null : account,
    caricamento: account === undefined,
    errore,
    ricarica,
    esci,
    imposta: setAccount,
    api,
  }), [account, errore, ricarica, esci, api]);

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

export function useAccount() {
  return useContext(Contesto) || VUOTO;
}

/** Vero se il profilo ha un abbonamento che permette di lavorare. */
export function abbonata(account) {
  return !!account?.azienda?.abbonata;
}

/** Vero se il piano dell'account include la funzione. */
export function consente(account, funzione) {
  return abbonata(account) && Array.isArray(account?.funzioni) && account.funzioni.includes(funzione);
}
